import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchUserOrders, updateOrderStatus } from "../../hooks/api";
import CustomSnackbar from "../../widgets/Snackbar/Snackbar";
import SecondaryButton from "../../widgets/Button/SecondaryButton/SecondaryButton";
import { ORDER_STATUSES } from "../../constants/constants";
import { PAGE_RELOAD_DELAY_MS } from "../../constants/config";

import "./FetchUserDetails.css";

const FetchUserDetails = () => {
  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [searchParams] = useSearchParams();
  const uamId = searchParams.get("uamId");

  const { data, error, isLoading } = useQuery(
    ["userOrders", uamId],
    () => fetchUserOrders(uamId),
    { enabled: !!uamId }
  );

  const filteredOrders =
    data?.data?.order_details?.filter(
      (order) =>
        order.status?.id === ORDER_STATUSES.CANCELLATION_REQUESTED ||
        order.status?.id === ORDER_STATUSES.CANCELLED
    ) || [];

  const mutation = useMutation({
    mutationFn: updateOrderStatus,
    onSuccess: (response) => {
      setSnackbar({
        open: true,
        message: response?.message || "Order cancelled successfully",
        severity: "success",
      });
      window.setTimeout(() => window.location.reload(), PAGE_RELOAD_DELAY_MS);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to update order",
        severity: "error",
      });
      window.setTimeout(() => window.location.reload(), PAGE_RELOAD_DELAY_MS);
    },
  });

  const handleAction = (orderId, action) => {
    mutation.mutate({ orderId, action });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <div className="order-cancellations-page">
      <div className="order-cancellations-card">
        <button
          type="button"
          className="order-cancellations-back"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ArrowBackIcon sx={{ fontSize: 32 }} aria-hidden />
          back
        </button>
        <header className="order-cancellations-header">
          <h1 className="order-cancellations-title">Order Cancellations</h1>
        </header>
        <div className="data-table-wrapper">
          <table className="data-table">
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Amount Paid</th>
            <th>Order Amount</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan="4" className="status-message">
                Loading...
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan="4" className="status-message error">
                Error: {error.message}
              </td>
            </tr>
          ) : filteredOrders.length > 0 ? (
            filteredOrders.map((row, index) => {
              const statusName = row.status?.name || "N/A";

              return (
                <tr key={index}>
                  <td className="data-table__order-id">
                    {row.order_id || "N/A"}
                  </td>
                  <td className="data-table__amount">{row.amount_paid || "N/A"}</td>
                  <td className="data-table__amount">{row.amount || "N/A"}</td>
                  <td className="data-table__status">
                    {statusName === "CANCELLATION_REQUESTED" ? (
                      <div className="status-cell">
                        <span className="status-label status-label--pending">
                          {statusName}
                        </span>
                        <div className="status-buttons-container">
                          <SecondaryButton
                            onClick={() =>
                              handleAction(row.order_id, "APPROVED")
                            }
                            disabled={mutation.isLoading}
                          >
                            Approve
                          </SecondaryButton>
                          <SecondaryButton
                            onClick={() =>
                              handleAction(row.order_id, "REJECTED")
                            }
                            disabled={mutation.isLoading}
                          >
                            Reject
                          </SecondaryButton>
                        </div>
                      </div>
                    ) : (
                      <span className="status-label status-label--done">
                        {statusName}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="4" className="no-data">
                No matching orders found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
        </div>

        <CustomSnackbar
          open={snackbar.open}
          onClose={handleCloseSnackbar}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      </div>
    </div>
  );
};

export default FetchUserDetails;
